import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { createPortal } from 'react-dom';
import { PdfPreviewModal } from '../../../rightToInformation/components/RtiContent/PdfDocumentTable';
import { searchMultilingualDocuments, loadSearchIndex, translateSearchQuery } from '../../../../search/searchIndex';
import './SiteSearch.css';

/** Header search: debounce input, cancel stale queries, and open page/PDF results. */
export default function SiteSearch({ language = 'en' }) {
  const inputId = useId();
  const location = useLocation();
  const root = useRef(null);
  const input = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle');
  const [limit, setLimit] = useState(15);
  const [translationUnavailable, setTranslationUnavailable] = useState(false);
  const [retry, setRetry] = useState(0);
  const [preview, setPreview] = useState(null);

  useEffect(() => { setOpen(false); setPreview(null); }, [location.pathname, location.hash]);
  useEffect(() => {
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, []);
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setTranslationUnavailable(false);
    if (!query.trim()) { setResults([]); setStatus('idle'); return; }
    setStatus('loading');
    setLimit(15);
    const timer = setTimeout(async () => {
      try {
        const [documents, translation] = await Promise.all([
          loadSearchIndex(),
          translateSearchQuery(query, language, { signal: controller.signal }),
        ]);
        if (cancelled) return;
        // Include the currently rendered language, including Bhashini translations.
        const main = document.querySelector('main, [role="main"], #main-content');
        const current = main?.textContent?.trim();
        const matches = searchMultilingualDocuments(current ? [...documents, {
          title: document.querySelector('main h1, #main-content h1, h1')?.textContent || 'Current page',
          text: current, url: location.pathname + location.hash, kind: 'Page',
        }] : documents, query, translation.query);
        setResults(matches);
        setStatus('ready');
        setTranslationUnavailable(translation.unavailable);
      } catch { if (!cancelled) setStatus('error'); }
    }, 450);
    return () => { clearTimeout(timer); cancelled = true; controller.abort(); };
  }, [query, language, retry, location.pathname, location.hash]);

  const close = () => setOpen(false);
  const openPreview = result => { close(); setPreview(result); };
  const closePreview = () => {
    input.current?.focus();
    close();
    setPreview(null);
  };
  return <><div className="site-search" ref={root} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) close();
  }} onKeyDown={event => {
    if (event.key === 'Escape') { input.current?.focus(); close(); event.stopPropagation(); }
    if (event.key === 'ArrowDown' && event.target === input.current) {
      const first = root.current.querySelector('.site-search-result');
      if (first) { event.preventDefault(); first.focus(); }
    }
  }}>
    <form role="search" onSubmit={event => { event.preventDefault(); setOpen(true); }}>
      <label htmlFor={inputId} className="sr-only">Search website pages and PDFs</label>
      <button type="submit" aria-label="Search website" title="Search website">
        <span className="sr-only">Search website</span>
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="10.5" cy="10.5" r="7.5" /><path d="m16 16 5 5" /></svg>
      </button>
      <input id={inputId} ref={input} type="search" value={query} onChange={event => { setQuery(event.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} placeholder="Search" aria-label="Search website pages and PDFs" title="Search website pages and PDFs" aria-controls={open ? 'site-search-results' : undefined} autoComplete="off" maxLength={200} />
    </form>
    {open && <section id="site-search-results" className="site-search-panel" aria-label="Website search results">
      <div className="site-search-status" role="status" aria-live="polite">
        {!query.trim() ? 'Search pages, content and PDFs in all 23 languages' : status === 'loading' ? 'Searching…' : status === 'error' ? 'Search could not load. Please try again.' : `${results.length} results`}
      </div>
      {translationUnavailable && status === 'ready' && <p className="site-search-hint" role="status">Language translation is temporarily unavailable. Showing direct text matches.</p>}
      {(status === 'error' || translationUnavailable) && <button className="site-search-more" type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>}
      {status === 'ready' && results.length === 0 && <p className="site-search-hint">No matches. Try another word, a document title or its original language.</p>}
      {status === 'ready' && <ul>{results.slice(0, limit).map(result => <li key={result.url}>
        <SearchResult result={result} onNavigate={close} onPreview={openPreview} />
      </li>)}</ul>}
      {status === 'ready' && results.length > limit && <button type="button" className="site-search-more" onClick={() => setLimit(value => value + 15)}>Show more results</button>}
    </section>}
  </div>
    {preview && createPortal(
      <PdfPreviewModal
        pdfDocument={{ title: preview.title, href: preview.url }}
        onClose={closePreview}
      />,
      document.body,
    )}
  </>;
}

/** Keep the search result design and preview PDFs without leaving the page. */
function SearchResult({ result, onNavigate, onPreview }) {
  const isPdf = result.kind === 'PDF';
  const Component = isPdf ? 'a' : Link;
  const linkProps = isPdf
    ? { href: result.url, 'aria-haspopup': 'dialog' }
    : { to: result.url };
  return <Component {...linkProps} className="site-search-result" onClick={event => {
    if (isPdf) { event.preventDefault(); onPreview(result); }
    else onNavigate();
  }}>
    <strong>{result.title}</strong>
    <span className="site-search-kind">{isPdf ? 'PDF · preview' : `Page · ${result.url}`}</span>
  </Component>;
}
